import { Dialog } from "@headlessui/react";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import React, { useEffect, useMemo, useState } from "react";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import IconClose from "@/icons/icon-close";
import { CarouselArrows, updateArrowStatus } from "~/components/editorials/carousel-utils/carousel-utils";
import { KikReviewsV2Media, KikReviewsV2ResponseReviewItem } from "~/shared/power-reviews-response.interface";

import Icon from "../../common/icon";
import stylesGallery from "./../product-gallery.module.scss";
import ReviewItemText from "./review-item-text";
import ReviewItemTextPreview from "./review-item-text-preview";
import styles from "./reviews-gallery-dialog.module.scss";

type Props = {
  media: KikReviewsV2Media[];
  startingIdForGallery: string | undefined;
  onClose: () => void;
  review?: KikReviewsV2ResponseReviewItem;
};

export default function ReviewsGalleryDialog(props: Props) {
  const t = useTranslations();
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
  const [arrowsStatus, setArrowsStatus] = useState({ arrowL: false, arrowR: false });
  const { review, startingIdForGallery, media, onClose } = props;

  useEffect(() => {
    if (swiperInstance) setArrowsStatus(updateArrowStatus(swiperInstance));
  }, [swiperInstance]);
  const changeExtension = (uri: string, extension: string): string => {
    return uri.replace(/\.[^.\/]+$/, `.${extension}`);
  };

  const initialSlideIndex = useMemo(() => {
    if (startingIdForGallery && media?.length > 0) {
      const startingMedia = media.find((m) => m.id === startingIdForGallery) || media[0];
      return media.indexOf(startingMedia);
    } else {
      return 0;
    }
  }, [startingIdForGallery, media]);

  if (media.length === 0) {
    return <></>;
  }

  return (
    <Dialog
      open={!!startingIdForGallery}
      onClose={() => {
        onClose();
      }}
      className={styles.galleryDialog}
    >
      <button
        aria-label={t("generic.close")}
        className={classNames(stylesGallery.galleryPanelClose, styles.galleryPanelClose)}
        onClick={onClose}
      >
        <Icon icon={IconClose} height={24} width={24} aria-hidden />
      </button>
      <Dialog.Panel className={styles.galleryMedia}>
        <Swiper
          onSwiper={setSwiperInstance}
          onTransitionEnd={(currentSwiper) => setArrowsStatus(updateArrowStatus(currentSwiper))}
          initialSlide={initialSlideIndex}
        >
          {media.map((m, i) => {
            return (
              <SwiperSlide key={m.id} className={styles.galleryMediaCardContainer}>
                <div className={styles.galleryMediaCard}>
                  <div className={styles.galleryMediaCardMedia}>
                    <picture>
                      <source type="image/webp" srcSet={changeExtension(m.uri, "webp")}></source>
                      <source type="image/jxr" srcSet={changeExtension(m.uri, "jxr")}></source>
                      <img
                        alt={m.caption + " - " + t("a11y.userSubmittedMedia", { index: i + 1, total: media.length })}
                        src={m.uri}
                      />
                    </picture>
                  </div>
                  <div className={styles.galleryMediaCardContent}>
                    {review ? (
                      <ReviewItemText review={review} inDialog></ReviewItemText>
                    ) : (
                      <ReviewItemTextPreview media={m} />
                    )}
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
        {swiperInstance && (
          <CarouselArrows
            swiperInstance={swiperInstance}
            arrowsStatus={arrowsStatus}
            className={styles.galleryArrows}
            isMobileProductGallery
          />
        )}
      </Dialog.Panel>
    </Dialog>
  );
}
